import { useRef, useState } from 'react'
import { motion, useInView } from 'framer-motion'

const ContactForm = () => {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState('')

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'required'
        if (value.trim().length < 2) return 'min 2 chars'
        return ''
      case 'email':
        if (!value) return 'required'
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'invalid email'
        return ''
      case 'message':
        if (!value.trim()) return 'required'
        if (value.trim().length < 10) return 'min 10 chars'
        return ''
      default:
        return ''
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    const error = validateField(name, value)
    setErrors(prev => ({ ...prev, [name]: error }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    const newErrors = {}
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key])
      if (error) newErrors[key] = error
    })

    setErrors(newErrors)

    if (Object.keys(newErrors).length === 0) {
      try {
        await new Promise(resolve => setTimeout(resolve, 2000))
        setSubmitStatus('success')
        setFormData({ name: '', email: '', message: '' })
      } catch {
        setSubmitStatus('error')
      }
    }

    setIsSubmitting(false)
  }

  const inputClasses = (fieldName) => {
    const base = 'w-full px-4 py-3 font-mono text-sm rounded border transition-all duration-300 focus:outline-none'
    const hasError = errors[fieldName]
    const hasValue = formData[fieldName]

    if (hasError) {
      return `${base} bg-[#12121A] border-[#F87171] text-[#E0E0E8] placeholder-[#555570] focus:border-[#F87171] focus:shadow-[0_0_10px_rgba(248,113,113,0.15)]`
    }
    if (hasValue && !hasError) {
      return `${base} bg-[#12121A] border-[#4ADE80] text-[#E0E0E8] placeholder-[#555570] focus:border-[#4ADE80] focus:shadow-[0_0_10px_rgba(74,222,128,0.15)]`
    }
    return `${base} bg-[#12121A] border-[#1E1E2E] text-[#E0E0E8] placeholder-[#555570] focus:border-[#00FFE0] focus:shadow-[0_0_10px_rgba(0,255,224,0.1)]`
  }

  return (
    <section ref={sectionRef} className="py-16 sm:py-24" style={{ background: 'var(--bg-surface)' }}>
      <div className="container mx-auto px-4 sm:px-6">

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-10 sm:mb-16"
        >
          <div className="font-mono text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
            <span style={{ color: 'var(--syntax-green)' }}>// </span>
            init connection
          </div>
          <h2 className="font-mono text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
            Let's <span style={{ color: 'var(--accent)' }}>Connect</span>
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-body)' }} className="text-lg max-w-2xl">
            Have a system that needs building? Let's talk about what I can ship for you.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-8 sm:gap-12 max-w-5xl">

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-3"
          >
            <div className="terminal-card">
              <div className="terminal-header">
                <div className="terminal-dot" style={{ background: '#FF5F57' }} />
                <div className="terminal-dot" style={{ background: '#FEBC2E' }} />
                <div className="terminal-dot" style={{ background: '#28C840' }} />
                <span className="ml-3">new-message.sh</span>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-5">

                {/* Name */}
                <div>
                  <label className="block font-mono text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                    <span style={{ color: 'var(--syntax-purple)' }}>const</span> name <span style={{ color: 'var(--text-muted)' }}>=</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className={inputClasses('name')}
                    placeholder="'Your name'"
                  />
                  {errors.name && (
                    <p className="font-mono text-xs mt-1" style={{ color: 'var(--syntax-red)' }}>
                      // error: {errors.name}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block font-mono text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                    <span style={{ color: 'var(--syntax-purple)' }}>const</span> email <span style={{ color: 'var(--text-muted)' }}>=</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={inputClasses('email')}
                    placeholder="'you@company.com'"
                  />
                  {errors.email && (
                    <p className="font-mono text-xs mt-1" style={{ color: 'var(--syntax-red)' }}>
                      // error: {errors.email}
                    </p>
                  )}
                </div>

                {/* Message */}
                <div>
                  <label className="block font-mono text-xs mb-2" style={{ color: 'var(--text-muted)' }}>
                    <span style={{ color: 'var(--syntax-purple)' }}>const</span> message <span style={{ color: 'var(--text-muted)' }}>=</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className={`${inputClasses('message')} resize-none`}
                    placeholder="'Tell me about your project...'"
                  />
                  {errors.message && (
                    <p className="font-mono text-xs mt-1" style={{ color: 'var(--syntax-red)' }}>
                      // error: {errors.message}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-terminal-filled justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <span className="font-mono">sending...</span>
                  ) : (
                    <span className="font-mono">$ send --message</span>
                  )}
                </button>

                {/* Status */}
                {submitStatus === 'success' && (
                  <div className="font-mono text-sm p-3 rounded border" style={{
                    color: 'var(--syntax-green)',
                    borderColor: 'rgba(74, 222, 128, 0.3)',
                    background: 'rgba(74, 222, 128, 0.05)'
                  }}>
                    ✓ Message sent successfully. I'll respond within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="font-mono text-sm p-3 rounded border" style={{
                    color: 'var(--syntax-red)',
                    borderColor: 'rgba(248, 113, 113, 0.3)',
                    background: 'rgba(248, 113, 113, 0.05)'
                  }}>
                    ✗ Something went wrong. Please try again.
                  </div>
                )}
              </form>
            </div>
          </motion.div>

          {/* Sidebar info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Response time */}
            <div className="terminal-card">
              <div className="terminal-header">
                <div className="terminal-dot" style={{ background: '#28C840' }} />
                <span className="ml-2">status</span>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full glow-pulse" style={{ background: 'var(--syntax-green)' }} />
                  <span className="font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Response within 24 hours
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ background: 'var(--syntax-blue)' }} />
                  <span className="font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Available for calls
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full" style={{ background: 'var(--syntax-purple)' }} />
                  <span className="font-mono text-xs" style={{ color: 'var(--text-secondary)' }}>
                    Remote & on-site
                  </span>
                </div>
              </div>
            </div>

            {/* Links */}
            <div className="terminal-card">
              <div className="terminal-header">
                <div className="terminal-dot" style={{ background: '#FEBC2E' }} />
                <span className="ml-2">links</span>
              </div>
              <div className="p-4 space-y-2">
                <a href="https://www.linkedin.com/in/ishay-almuly/" target="_blank" rel="noopener noreferrer" className="block font-mono text-xs py-2 px-3 rounded transition-all duration-300 hover:bg-[rgba(0,255,224,0.05)]" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--accent)' }}>&gt;</span> LinkedIn
                </a>
                <a href="https://github.com/ishayoyo" target="_blank" rel="noopener noreferrer" className="block font-mono text-xs py-2 px-3 rounded transition-all duration-300 hover:bg-[rgba(0,255,224,0.05)]" style={{ color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--accent)' }}>&gt;</span> GitHub
                </a>
              </div>
            </div>

            {/* Signature */}
            <div className="font-mono text-xs p-4" style={{ color: 'var(--text-muted)' }}>
              <div>---</div>
              <div className="mt-2" style={{ color: 'var(--accent)' }}>Ishay Almuly</div>
              <div>Backend & Systems Engineer</div>
              <div className="mt-1" style={{ color: 'var(--text-muted)' }}>
                I build the infrastructure organizations run on.
              </div>
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  )
}

export default ContactForm
